import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { NAV } from "@/constants/testIds";

// Work lives on the homepage, so it links to the section rather than its own route.
const links = [
    { id: "work", label: "Work", to: "/#work", anchor: true },
    { id: "approach", label: "Approach", to: "/approach" },
    { id: "about", label: "About", to: "/about" },
    { id: "careers", label: "Careers", to: "/careers" },
    { id: "contact", label: "Contact", to: "/contact" },
];

const testIdMap = {
    work: NAV.linkWork,
    approach: NAV.linkApproach,
    about: NAV.linkAbout,
    careers: NAV.linkCareers,
    contact: NAV.linkContact,
};

export default function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
                scrolled
                    ? "bg-[#F6F3EC]/90 backdrop-blur-sm border-b border-[#E6E2D6]"
                    : "bg-transparent"
            }`}
        >
            <div className="mx-auto max-w-[1800px] px-6 md:px-10 lg:px-16">
                <div className="flex items-center justify-between h-16 md:h-20">
                    <Link
                        to="/"
                        data-testid={NAV.logo}
                        className="font-sans-tight text-[15px] tracking-[0.36em] uppercase text-[#1B1B1B] font-medium"
                    >
                        Depmont
                    </Link>

                    <nav className="hidden md:flex items-center gap-12 lg:gap-14">
                        {links.map((l) =>
                            l.anchor ? (
                                <Link
                                    key={l.id}
                                    to={l.to}
                                    data-testid={testIdMap[l.id]}
                                    className="font-sans-tight text-[15px] tracking-[0.02em] text-[#1B1B1B] link-underline"
                                >
                                    {l.label}
                                </Link>
                            ) : (
                                <NavLink
                                    key={l.id}
                                    to={l.to}
                                    data-testid={testIdMap[l.id]}
                                    className={({ isActive }) =>
                                        `font-sans-tight text-[15px] tracking-[0.02em] text-[#1B1B1B] link-underline ${
                                            isActive ? "link-underline-active" : ""
                                        }`
                                    }
                                >
                                    {l.label}
                                </NavLink>
                            ),
                        )}
                    </nav>

                    <button
                        type="button"
                        data-testid={NAV.mobileToggle}
                        onClick={() => setOpen((v) => !v)}
                        className="md:hidden flex flex-col gap-[5px] p-2 -mr-2"
                        aria-label="Menu"
                    >
                        <span
                            className={`block h-px w-6 bg-[#1B1B1B] transition-transform ${open ? "translate-y-[6px] rotate-45" : ""}`}
                        />
                        <span
                            className={`block h-px w-6 bg-[#1B1B1B] transition-opacity ${open ? "opacity-0" : ""}`}
                        />
                        <span
                            className={`block h-px w-6 bg-[#1B1B1B] transition-transform ${open ? "-translate-y-[6px] -rotate-45" : ""}`}
                        />
                    </button>
                </div>
            </div>

            {/* Mobile Menu */}
            <div
                data-testid={NAV.mobileMenu}
                className={`md:hidden overflow-hidden transition-[max-height] duration-500 ease-editorial bg-[#F6F3EC] ${
                    open ? "max-h-[480px]" : "max-h-0"
                }`}
            >
                <div className="px-6 pt-4 pb-8 border-t border-[#E6E2D6]">
                    {links.map((l, i) => (
                        <Link
                            key={l.id}
                            to={l.to}
                            data-testid={`${testIdMap[l.id]}-mobile`}
                            onClick={() => setOpen(false)}
                            className="block py-4 font-serif-editorial text-3xl text-[#1B1B1B] border-b border-[#E6E2D6] last:border-none"
                        >
                            <span className="text-[#A8844F] text-xs font-sans-tight tracking-[0.2em] mr-4 align-middle">
                                0{i + 1}
                            </span>
                            {l.label}
                        </Link>
                    ))}
                </div>
            </div>
        </header>
    );
}
